<template>
  <div class="provider-container">
    <slot />
  </div>
</template>

<script>
import { getSubscriptionInfo, getUserUsage } from "@/api";
import {
  BASIC_PLAN,
  PRICING_PLANS,
  SSK_PLAN_CHANGE_PENDING,
  TRIAL_PLAN,
} from "@/constants";

export default {
  name: "AccountProvider",
  provide() {
    return {
      accountProvider: this.accountProvider,
      refreshUsageIfNeeded: this.refreshUsageIfNeeded,
    };
  },

  data() {
    return {
      gradingWasActive: false,
      accountProvider: {
        currentSubscriptionAmount: 0,
        paymentInterval: "month",
        subscription: {},
        subscriptionLoading: true,
        nextBillingDate: null,
        manualSubscriptionChanges: false,
        usage: {},
        availablePlans: PRICING_PLANS.teachers.reduce((acc, plan) => {
          acc[plan.id] = plan;

          return acc;
        }, {}),
        isUserSubscribed: false,
        showSubscriptionActions: false,
      },
    };
  },

  computed: {
    user() {
      return this.$store.state.user || {};
    },
    subscriptionTrigger() {
      return `${this.user.id}|${this.user.plan}`;
    },
  },

  watch: {
    subscriptionTrigger: {
      handler() {
        const pendingRaw = sessionStorage.getItem(SSK_PLAN_CHANGE_PENDING);
        if (pendingRaw && this.user.id && this.user.plan) {
          const [storedUserId, storedPlan] = pendingRaw.split("|");
          if (storedUserId === this.user.id && storedPlan === this.user.plan) {
            return;
          }
          sessionStorage.removeItem(SSK_PLAN_CHANGE_PENDING);
        }
        this.initSubscriptionData();
      },
      immediate: true,
    },
  },

  created() {
    this.$eventBus.$on("queued-essays-changed", this.onQueuedEssaysChanged);
  },

  beforeDestroy() {
    this.$eventBus.$off("queued-essays-changed", this.onQueuedEssaysChanged);
  },

  methods: {
    async refreshUsage() {
      this.gradingWasActive = false;
      try {
        const { data: usageData } = await getUserUsage();
        this.accountProvider.usage = usageData;
      } catch {
        // silent — usage refresh is best-effort
      }
    },

    async refreshUsageIfNeeded() {
      if (!this.gradingWasActive || !this.user.id) {
        return;
      }
      await this.refreshUsage();
    },

    async onQueuedEssaysChanged(queuedCount) {
      if (queuedCount > 0) {
        this.gradingWasActive = true;
        return;
      }
      if (!this.gradingWasActive || !this.user.id) {
        return;
      }
      const essaysUsage = this.accountProvider.usage?.essaysUsage;
      if (!essaysUsage?.limit) {
        return;
      }
      if (essaysUsage.count >= essaysUsage.limit) {
        return;
      }
      const usedPercent = (essaysUsage.count / essaysUsage.limit) * 100;
      if (usedPercent <= 75) {
        return;
      }
      await this.refreshUsage();
    },

    async initSubscriptionData() {
      if (!this.user.id) {
        return;
      }

      // note: can't fetch subscription if the user
      // is not a paid user with a subscription
      const { stripeSubscriptionId } = this.user;

      try {
        const { data: usageData } = await getUserUsage();
        this.accountProvider.usage = usageData;
      } catch {
        // silent — usage fetch is best-effort
      }

      const isBasicOrTrial = [BASIC_PLAN, TRIAL_PLAN].includes(this.user.plan);
      this.accountProvider.isUserSubscribed =
        !!this.user.plan && !isBasicOrTrial;
      const isStripeSubscription =
        this.accountProvider.isUserSubscribed && this.user.stripeSubscriptionId;
      this.accountProvider.showSubscriptionActions =
        isStripeSubscription || isBasicOrTrial;

      if (!this.basicPlan && stripeSubscriptionId) {
        try {
          const { data } = await getSubscriptionInfo();

          this.accountProvider.subscription = data;
          // interval can be either "month" or "year"
          this.accountProvider.currentSubscriptionAmount = (
            data.plan.amount / 100
          ).toFixed(2);
          this.accountProvider.paymentInterval = data.plan.interval;

          this.accountProvider.nextBillingDate = new Date(
            data.current_period_end * 1000
          );
          this.accountProvider.subscriptionLoading = false;
        } catch (error) {
          this.accountProvider.subscriptionLoading = false;
          this.accountProvider.manualSubscriptionChanges = true;
        }
      } else if (!this.basicPlan && !stripeSubscriptionId) {
        this.accountProvider.subscriptionLoading = false;
        this.accountProvider.manualSubscriptionChanges = true;

        // Pricing for US Stripe Customers - we don't have a stripe id for them
        if (this.litePlan) {
          this.accountProvider.currentSubscriptionAmount = "8.99";
        } else if (this.proPlan) {
          this.accountProvider.currentSubscriptionAmount = "19.99";
        } else {
          this.accountProvider.currentSubscriptionAmount = "49.99";
        }
      } else {
        // user is on basic plan set loading to false
        // since no fetch needs to happen

        this.accountProvider.subscriptionLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.provider-container {
  display: contents;
}
</style>
